import { useMemo } from "react";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { lead_spesific_key, child_spesific_key } from "@/config/endpoints";

export interface CountryCustomField {
  id: number;
  name: string;
  options: { id: number; value: string }[];
}

/**
 * Locates the tenant's "Country" custom field — the one a study-abroad lead
 * uses for its preferred / destination country. Lead-level fields are checked
 * first, then child-level; the first SELECTOR field with options wins.
 *
 * Detection is two-tier so it works regardless of how the tenant configured
 * the field:
 *  1. An explicitly tagged field (`spesificTypes === "Country"`), then
 *  2. a SELECTOR field whose NAME mentions "country" (e.g. "Country",
 *     "Preferred Country", "Destination Country"). Most tenants create this
 *     field as a plain selector (`spesificTypes: "Normal"`) and never tag it,
 *     so a tag-only match left the header picker silently hidden even though
 *     the side filter panel — which keys off the field NAME — showed it fine.
 *  "Country Code" (the phone dialling-code filter) is excluded explicitly.
 *
 * Returns `null` when the tenant has no such field, in which case the header
 * Country picker renders nothing. `enabled` gates the underlying queries so
 * non-study-abroad tenants don't fetch custom fields just to hide the picker.
 */
export function useCountryCustomField(enabled: boolean) {
  const lead = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
    type: "SELECTOR",
    isActive: enabled,
  });
  const child = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    type: "SELECTOR",
    isActive: enabled,
  });

  const field = useMemo<CountryCustomField | null>(() => {
    const hasOptions = (f: any) => (f?.options?.length ?? 0) > 0;
    const byTag = (fields: any[] | undefined) =>
      (fields ?? []).find(
        (f: any) => f?.spesificTypes === "Country" && hasOptions(f)
      );
    const byName = (fields: any[] | undefined) =>
      (fields ?? []).find((f: any) => {
        const name = String(f?.name ?? "").toLowerCase();
        return hasOptions(f) && name.includes("country") && !name.includes("code");
      });
    // Prefer the explicit tag (lead, then child); fall back to a name match.
    const found =
      byTag(lead.spesificFields) ??
      byTag(child.spesificFields) ??
      byName(lead.spesificFields) ??
      byName(child.spesificFields);
    if (!found) return null;
    return {
      id: found.id,
      name: found.name,
      options: (found.options ?? []).map((o: any) => ({
        id: o.id,
        value: o.value,
      })),
    };
  }, [lead.spesificFields, child.spesificFields]);

  return { field, isLoading: lead.isLoading || child.isLoading };
}
